import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { teacherService } from '../../services/classroom/teacherService';

const panelStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--outline)',
  borderRadius: 12,
  padding: '0.9rem',
};

const ClassroomTeacherAnalyticsPage: React.FC = () => {
  const gradesQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'grades'],
    queryFn: teacherService.getGrades,
  });

  const overviewQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'overview'],
    queryFn: teacherService.getOverview,
  });

  const averageScore = useMemo(() => {
    const scores = (gradesQuery.data ?? []).map((row) => row.score).filter((score): score is number => score != null);
    if (scores.length === 0) return 0;
    return Math.round((scores.reduce((sum, score) => sum + score, 0) / scores.length) * 10) / 10;
  }, [gradesQuery.data]);

  if (gradesQuery.isLoading || overviewQuery.isLoading) return <div>Loading analytics...</div>;
  if (gradesQuery.isError || overviewQuery.isError) return <div>Unable to load analytics.</div>;

  return (
    <section style={{ display: 'grid', gap: '1rem' }}>
      <article style={panelStyle}>
        <h3 style={{ marginTop: 0 }}>Class performance snapshot</h3>
        <p style={{ color: 'var(--textSecondary)' }}>
          Average score per assignment and class completion at a glance.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '0.8rem' }}>
          <div style={{ border: '1px solid var(--outline)', borderRadius: 8, padding: '0.7rem' }}>
            <h4 style={{ marginTop: 0 }}>Average Score</h4>
            <strong style={{ fontSize: '1.5rem' }}>{averageScore}</strong>
          </div>
          {(overviewQuery.data ?? []).map((item) => {
            const completion = Math.min(100, Math.round((item.recentSubmissions / (item.activeStudents || 1)) * 100));
            return (
              <div key={item.id} style={{ border: '1px solid var(--outline)', borderRadius: 8, padding: '0.7rem' }}>
                <h4 style={{ marginTop: 0 }}>{item.name}</h4>
                <p style={{ margin: 0, color: 'var(--textSecondary)' }}>Completion {completion}%</p>
                <div style={{ height: 8, background: 'var(--outline)', borderRadius: 999, overflow: 'hidden' }}>
                  <span
                    style={{
                      display: 'block',
                      height: '100%',
                      width: `${completion}%`,
                      background: 'var(--primary)',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </article>
    </section>
  );
};

export default ClassroomTeacherAnalyticsPage;
